import { backend } from '@/services/backend'
import { platform } from '@/services/platform'
import { resetAppCache } from '@/helpers/cache'
import { apStyleDate } from '@/helpers/time'
import { AdjustWrapper } from '@/analytics/adjust'
import { Mixpanel } from '@/analytics/mixpanel'
import { trackSubscribe } from '@/analytics/logic'

import {
  AppStore,
  AppStorePluginProduct,
  UserCancelledError,
} from '@/services/cordova.purchase.types'
import { Sentry } from '@/services/sentry'

import {
  Billing,
  Plan,
  BillingPlans,
  BillingType,
  StripeCard,
  BillingPlanID,
} from './interfaces'
import {
  PlanView,
  FreePlanView,
  MonthlyPlanView,
  AnnualPlanView,
  PlanPrice,
} from './billing.plans'
export { PlanView, AnnualPlanView, MonthlyPlanView }
import { formatTrialDate, trialEndDiff, futureDate } from '@/helpers/time'
import {
  iosExternalLinkPayment16218Variant,
  trialByPlan15535Variant,
} from '@/services/ab/experiments'
import { storage } from '@/helpers/storage'
import {
  getTrialByPlanVariantConfig,
  trialByPlan15535Treatment,
  TrialByPlanExperimentVariantConfig,
} from '@/services/ab/15535_trial_by_plan'
import { router } from '@/helpers/router'

export enum BillingAlert {
  none = 'none',
  canceledTrial = 'canceledTrial',
  canceledPaid = 'canceledPaid',
  downgraded = 'downgraded',
  upgraded = 'upgraded',
  subscribed = 'subscibed',
  resubscribed = 'resubscibed',
  subscribedAndRedirect = 'subscibedAndRedirect',
}

/**
 * Check if we have Apple subscription in the web or Android app.
 *
 * We display a special "manage your account on iOS" page in this case
 */
export async function isAppleWeb(billing: Billing): Promise<boolean> {
  const info = await platform.deviceInfo()

  return (
    info.platform !== 'ios' &&
    billing.billing_type === 'apple' &&
    billing.plan_id !== 'plan_free'
  )
}

/**
 * Check if we have a Google subscription in the web or iOS app.
 *
 * We display a special "manage your account on Android" page in this case.
 */
export async function isGoogleWeb(billing: Billing): Promise<boolean> {
  const info = await platform.deviceInfo()

  return (
    info.platform !== 'android' &&
    billing.billing_type === 'google' &&
    billing.plan_id !== 'plan_free'
  )
}

/**
 * Factory function to create platform-specific billing view.
 *
 * Creates AppStoreBillingView for iOS and BillingView for other platforms.
 */
export async function createBilling(billing: Billing): Promise<BillingView> {
  const info = await platform.deviceInfo()

  // - if billing.billing_type === 'stripe' - always use Stripe billing
  if (billing.billing_type === 'stripe') {
    return new BillingView(billing)
  }

  // TODO: for existing subscription:
  // - if billing.billing_type === 'apple' and platform is not iOS - show
  // special version

  // Use Apple Payments on iOS and Google Play Billing on Android.
  // We determine which plugin version to use based on the mobile app version.
  // In versions 7 and beyond, we use v13 of the plugin, but in previous
  // versions, we use v11.
  if (info.platform === 'ios' || info.platform === 'android') {
    let purchaseModule
    const majorVersion = parseInt(info.appVersion.split('.')[0])
    if (majorVersion >= 7) {
      purchaseModule = await import('@/services/cordova.purchase')
    } else {
      purchaseModule = await import('@/services/cordova.purchase.v11')
    }
    const appStore = purchaseModule.appStore
    await appStore.init(info.platform)
    const view = new AppStoreBillingView(billing, appStore, info.platform)
    return view
  }

  // Use Stripe otherwise.
  const view = new BillingView(billing)
  return view
}

/**
 * Billing view.
 *
 * Note: we use "!" modifier for properties to avoid an error:
 *   @ts-ignore 2564: property has no initializer
 *   and is not assinged in the constructor (which is not true).
 * In this case, properties are assigned in the constructor, but
 * inderectly, in the `this.update` call.
 */
export class BillingView implements Billing {
  public user_id!: string
  public plan_id!: BillingPlanID
  public cancel_at_period_end!: boolean
  public change_plan_at_period_end!: boolean
  public current_period_end!: number
  public current_period_end_datetime!: string
  public current_period_amount!: number
  public is_trial!: boolean
  public is_limited!: boolean
  public status!: string
  public status_message!: string
  public stripe_payment_type?: string
  public trial_start!: number | null
  public trial_start_datetime!: string | null
  public trial_end!: number | null
  public trial_end_datetime!: string | null
  public card_data?: StripeCard
  public billing_plans!: BillingPlans
  public billing_type!: BillingType

  protected _freePlan?: FreePlanView
  protected _monthlyPlan?: MonthlyPlanView
  protected _annualPlan?: AnnualPlanView
  protected _currentPeriodPlan?: PlanView

  public cardEditMode!: boolean

  // Set to true right after the subscription.
  public alert: BillingAlert = BillingAlert.none

  private _trialConfig: TrialByPlanExperimentVariantConfig | null = null

  public platform: 'ios' | 'android' | 'web' = 'web'

  constructor(billing: Billing) {
    this.update(billing)

    this.initTrialLengths()
  }

  public async initTrialLengths(): Promise<void> {
    const variant = trialByPlan15535Variant()
    if (!variant) {
      return
    }

    const config = getTrialByPlanVariantConfig(variant)

    if (config) {
      this._trialConfig = config
    }
  }

  public update(billing: Billing): void {
    this.user_id = billing.user_id
    this.plan_id = billing.plan_id
    this.cancel_at_period_end = billing.cancel_at_period_end
    this.change_plan_at_period_end = billing.change_plan_at_period_end
    this.current_period_end = billing.current_period_end
    this.current_period_end_datetime = billing.current_period_end_datetime
    this.current_period_amount = billing.current_period_amount
    this.is_trial = billing.is_trial
    this.is_limited = billing.is_limited
    this.status = billing.status
    this.status_message = billing.status_message
    this.stripe_payment_type = billing.stripe_payment_type
    this.trial_start = billing.trial_start
    this.trial_start_datetime = billing.trial_start_datetime
    this.trial_end = billing.trial_end
    this.trial_end_datetime = billing.trial_end_datetime
    this.card_data = billing.card_data
    this.billing_plans = billing.billing_plans
    this.billing_type = billing.billing_type

    // If we have card already - hide the card input.
    if (this.card_data && this.card_data.last4) {
      this.cardEditMode = false
    } else {
      this.cardEditMode = true
    }
  }

  get freePlan(): PlanView {
    if (!this._freePlan) {
      this._freePlan = new FreePlanView(this.billing_plans)
    }
    return this._freePlan
  }

  get monthlyPlan(): PlanView {
    if (!this._monthlyPlan) {
      this._monthlyPlan = MonthlyPlanView.createView(this.billing_plans)
    }
    return this._monthlyPlan
  }

  get annualPlan(): PlanView {
    if (!this._annualPlan) {
      this._annualPlan = AnnualPlanView.createView(this.billing_plans)
    }
    return this._annualPlan
  }

  getPlanByID(id: BillingPlanID): PlanView {
    switch (id) {
      case 'plan_free':
        return this.freePlan
      case 'plan_monthly':
        return this.monthlyPlan
      case 'plan_annual':
        return this.annualPlan
      default:
        throw new Error(`Invalid Plan ID: ${id}`)
    }
  }

  /**
   * Current billing plan
   */
  get plan(): PlanView {
    const plan = this.plans.find((p) => p.id === this.plan_id)
    if (plan === undefined) {
      throw new Error('Wrong billing plan id: ' + this.plan_id)
    }
    return plan
  }

  /**
   * PlanView representing the billing data of the currently
   * active billing period.
   *
   * We use this to handle the difference between the plan's
   * regular price and the user's currently active billing period,
   * which can have discount data that differs from the plan's
   * regular prices.
   */
  get annualPlanCurrentPeriod(): PlanView | undefined {
    if (!this._currentPeriodPlan && this.plan_id === 'plan_annual') {
      this._currentPeriodPlan = new AnnualPlanView(this.current_period_amount)
    }

    return this._currentPeriodPlan
  }

  /**
   * Get the price of the currently active billing period.
   */
  get currentPeriodPrice(): PlanPrice {
    if (this.annualPlanCurrentPeriod && this.annualPlanCurrentPeriod.amount) {
      return this.annualPlanCurrentPeriod.price
    }

    return this.plan.price
  }

  /**
   * The price that will be charged at the end of the user's
   * currently active billing period.
   */
  get nextBillingPrice(): PlanPrice {
    if (this.is_trial) {
      return this.currentPeriodPrice
    }

    return this.plan.price
  }

  /**
   * Billing plan name to show in UI.
   */
  get planName(): string {
    let name = this.plan.nickname
    if (this.cancel_at_period_end) {
      name = name + ' (canceled)'
    } else if (
      this.change_plan_at_period_end &&
      this.plan_id === 'plan_annual'
    ) {
      name = name + ' (scheduled to downgrade)'
    } else if (
      this.change_plan_at_period_end &&
      this.plan_id === 'plan_monthly'
    ) {
      name = name + ' (scheduled to upgrade)'
    }
    return name
  }

  /**
   * Is current billing plan paid?
   */
  get isPaidPlan(): boolean {
    return this.plan_id !== 'plan_free'
  }

  /**
   * Additional billing status message.
   */
  get statusMessage(): string {
    if (this.status === 'subscription_bad' && this.billing_type === 'google') {
      return `We ran into an issue charging your card for this subscription, so we've downgraded your account to the Free plan. Please <a href="${this.paymentProviderSettingsUrl}">fix your payment method on Google Play</a>, or contact us at <a href="mailto:help@shortform.com">help@shortform.com</a> to resolve the issue.`
    }
    if (this.status === 'subscription_bad') {
      return `We ran into an issue charging your card for this subscription, so we've downgraded your account to the Free plan. Please contact us at <a href="mailto:help@shortform.com">help@shortform.com</a> to resolve the issue.`
    }
    return ''
  }

  get cancelDiscountAlertText(): string {
    if (this.plan_id === 'plan_annual') {
      return 'You got the first 3 months for free! Wise choice!'
    }

    return 'You saved 50% on your first month! Wise choice!'
  }

  /**
   * Title to show at the top of the billing page.
   */
  get subscriptionBoxTitle(): string {
    if (trialByPlan15535Treatment()) {
      return 'Start Learning Faster Today'
    }
    return this.isTrialOver
      ? 'Subscribe for full access'
      : 'Start your 5-day free trial'
  }

  /**
   * Message to show at the top of the billing page.
   */
  get subscriptionBoxInfo(): string {
    if (this.isTrialOver) {
      return 'Get full access to Shortform and supercharge your learning. Cancel anytime.'
    }

    if (trialByPlan15535Variant() === 'D') {
      return `Get full access to Shortform. Annual plans won't be charged until the free trial ends on <strong>${this.freeTrialExpiry(
        'annual',
      )}</strong>. Monthly plans won't be charged until free trial ends on <strong>${this.freeTrialExpiry()}</strong>.`
    } else if (trialByPlan15535Treatment()) {
      return `Get full access to Shortform. Annual plans won't be charged until the free trial ends on <strong>${this.freeTrialExpiry(
        'annual',
      )}</strong>. Monthly plans will be charged immediately.`
    }

    return `The trial gives you free, full access to Shortform. You won't be charged until your free trial ends on ${this.freeTrialExpiry()}. Cancel anytime.`
  }

  /**
   * List of all billing plans.
   */
  get plans(): PlanView[] {
    // It would be better to query these on the backend from Stripe
    // and add to the billing data, but for now we just fill them
    // in here for simplicity and speed.
    // Note: actual price is on Stripe, if we change the price, we
    // need to update it here too.
    // Note: for now we don't include annual plan here.
    return [this.freePlan, this.monthlyPlan, this.annualPlan]
  }

  /**
   * Get the price difference between the annual and monthly plans.
   */
  get switchToAnnualSavings(): { amount: number; percent: number } {
    return this.compareSavings(this.annualPlan, this.monthlyPlan)
  }

  /**
   * Get the price difference between the two given plans.
   */
  compareSavings(
    planA: PlanView,
    planB: PlanView,
  ): { amount: number; percent: number } {
    const priceA = planA.normalizedAmount.perYear
    const priceB = planB.normalizedAmount.perYear

    const amount = priceB - priceA
    const percent = Math.round((amount / priceB) * 100)

    return { amount, percent }
  }

  /**
   * Trial mode description (not started, active, orver) with
   * trial mode dates.
   */
  get trialMode(): string {
    if (this.trial_end === null) {
      return 'Not Started'
    }
    if (this.is_trial === true) {
      return `Active (${this.trialStart} - ${this.trialEnd(this.plan_id)})`
    }
    return `Over (${this.trialEnd(this.plan_id)})`
  }

  /**
   * Is trial over?
   *
   * Returns 'true' if the user has finished the trial period.
   *
   * Note: this is different from just `!this.is_trial` as we need
   * to exclude the case when the trial has not been started yet.
   *
   * This property additionally checks the `trial_end` to make sure
   * that it is actually over (the user had the trial in the past
   * and it is not active anymore).
   */
  get isTrialOver(): boolean {
    if (this.trial_end !== null && this.is_trial === false) {
      return true
    }
    return false
  }

  /**
   * End of the billing period when new payment is to be performed or
   * plan to be canceled.
   */
  get billingPeriodEnd(): string {
    return formatTrialDate(this.current_period_end_datetime)
  }

  /**
   * Start of the trial period.
   */
  get trialStart(): string {
    if (this.trial_start_datetime) {
      return formatTrialDate(this.trial_start_datetime)
    }
    return ''
  }

  /**
   * End of the trial period.
   */
  trialEnd(planId: string): string {
    if (this.trial_end_datetime) {
      return formatTrialDate(this.trial_end_datetime)
    }
    if (!this.isPaidPlan && !this.isTrialOver) {
      // This is when the trial is not started yet, but
      // we want display the date it ends if started now.
      const trialLength = this.trialLength(
        planId === 'plan_monthly' ? 'monthly' : 'annual',
      )
      return futureDate(trialLength || 0)
    }
    return ''
  }

  /**
   * Whether to show prices after discount has been applied.
   */
  get showPricesAfterDiscount(): boolean {
    return (
      storage.getItem('sf_cancel_discount') === 'true' &&
      this.isTrialOver &&
      this.status !== 'subscription_bad'
    )
  }

  /**
   * Days until the end of trial.
   */
  daysUntilTrialEnd(): string {
    if (this.trial_end_datetime) {
      return trialEndDiff(this.trial_end_datetime)
    }
    return ''
  }

  /**
   * Is given `plan` currently active?
   */
  isActive(plan: Plan): boolean {
    return plan.id === this.plan_id
  }

  private get _isExternalApplePage16218(): boolean {
    return router().currentRoute.value?.name === 'billing_apple_external'
  }

  /**
   * Subscribe to the specified billing plan.
   */
  async subscribeTo(plan: PlanView): Promise<void> {
    const isNewUser = this.trialMode === 'Not Started'
    const wasCanceled = this.cancel_at_period_end === true

    const data = {
      id: plan.id,
      amount: plan.amount,
      nickname: plan.nickname,
    }
    const response = await backend.subscribe(data)

    // If we are in the external billing page, close the in-app browser
    // and continue this logic in the native app.
    if (this._isExternalApplePage16218) {
      // We use the `?close-browser` query in `@/init/native.app.ts` to
      // close the in-app browser.
      window.location.href = 'shortform://?close-browser16218'
      return
    }

    await trackSubscribe(data, this)
    this.update(response)

    if (plan.id === 'plan_free') {
      if (this.is_trial) {
        this.alert = BillingAlert.canceledTrial
      } else {
        this.alert = BillingAlert.canceledPaid
      }
    } else {
      // Show subscribed alert and redirect to books page after delay.
      if (isNewUser) {
        // We track the Adjust Trial Start (card save) event only for new
        // users.
        await AdjustWrapper.trackCardSave()
        this.alert = BillingAlert.subscribedAndRedirect
      } else if (wasCanceled) {
        this.alert = BillingAlert.resubscribed
      } else {
        this.alert = BillingAlert.subscribed
      }
    }

    // Reset app cache to force API data to reload.
    // When we switch from free to paid and back, the content changes,
    // so we need to get the fresh data before showing it.
    await resetAppCache()
  }

  async applyCancelDiscount(): Promise<void> {
    try {
      const billing = await backend.applyCancelDiscount()
      storage.setItem('sf_cancel_discount', 'true')
      this.update(billing)
    } catch (error) {
      throw error
    }
  }

  get afterCancelTrialAlert(): boolean {
    return this.alert === BillingAlert.canceledTrial
  }

  get afterCancelPaidAlert(): boolean {
    return this.alert === BillingAlert.canceledPaid
  }

  get subscribedAlert(): boolean {
    return this.alert === BillingAlert.subscribed
  }

  get resubscribedAlert(): boolean {
    return this.alert === BillingAlert.resubscribed
  }

  get downgradedAlert(): boolean {
    return this.alert === BillingAlert.downgraded
  }

  get upgradedAlert(): boolean {
    return this.alert === BillingAlert.upgraded
  }

  get subscribedAlertAndRedirect(): boolean {
    return this.alert === BillingAlert.subscribedAndRedirect
  }

  isAppStore(): boolean {
    return false
  }

  trialLength(plan: 'annual' | 'monthly'): number | null {
    if (!this._trialConfig) {
      return 5
    }

    if (this.showExternalPayment16218) {
      // Always show the `web` trial dates in the plan cards
      // when the user is in the `B` variant of the IOS external
      // payments experiment.
      return this._trialConfig[plan]['web'].trial_days
    }

    const parsedPlatform = this.platform === 'ios' ? 'ios' : 'web'

    return this._trialConfig[plan][parsedPlatform].trial_days
  }

  // If the user has not started their free trial:
  //   Return the (ap formatted) date that the trial period would expire on,
  //   if they signed up today.
  //
  // If the user already started their free trial, and they still have time
  // left on it:
  //   Return the (ap formatted) date that it is set to expire.
  //
  // If the users free trial already expired:
  //   Return `null`
  //
  // This function is used to display (or not) a "free trial timeline"
  // widget on the billing page. See #5656 for details.
  freeTrialExpiry(plan: 'annual' | 'monthly' = 'monthly'): string | null {
    const trialLength = this.trialLength(plan)

    if (this.trial_end === null) {
      // If the user has not started their trial yet, then assuming they
      // signed up today, it would expire in `trialLength` days
      const hypotheticalExpiry = new Date()
      const futureDate = hypotheticalExpiry.getDate() + (trialLength || 0)
      hypotheticalExpiry.setDate(futureDate)
      return apStyleDate(hypotheticalExpiry)
    } else {
      // Whether the trial period is currently active or not
      // we know that it was once started, which means that the expiry date
      // is now fixed and cannot be changed.
      const trialEndDate = new Date(this.trial_end * 1000)
      const now = new Date()
      const expiresInFuture = trialEndDate > now
      if (expiresInFuture) {
        // The user still has time left on their free trial, so we want
        // to show them when it will expire
        return apStyleDate(trialEndDate)
      } else {
        // The users trial period is already over, so the widget should
        // not be shown at all.
        return null
      }
    }
  }

  /**
   * Determines if a discount banner on the billing page should be shown for
   * the free plan. If true, a banner with a message like
   * "You've unlocked 21% off your subscription" will be shown.
   *
   * For Stripe (i.e. in browsers) this depends on `billing_plans.discount.prompt` being
   * defined.
   *
   * For Apple payments (and soon Google payments), i.e. in mobile apps, this is
   * always false as we do not support applying discounts there.
   */
  get showDiscountBanner(): boolean {
    return !!this.billing_plans.discount.prompt
  }

  /**
   * Returns a link to the page provided by the payment provider where users can
   * manage their subscription. Available only for Apple and Google billing.
   */
  get paymentProviderSettingsUrl(): string {
    if (this.billing_type === 'apple') {
      return 'https://apps.apple.com/account/subscriptions'
    } else if (this.billing_type === 'google') {
      return `https://play.google.com/store/account/subscriptions?sku=${this.plan_id}&package=com.shortform.app`
    }
    // Else there is no settings page by the payment provider.
    Sentry.captureException(
      `Unexpected code path. No settings page for billing_type '${this.billing_type}'.`,
    )
    return ''
  }

  /**
   * We use this method to hide parts of the UI when it is not possible to switch
   * billing plans with Google billing.
   *
   * It is not possible to switch billing plans when:
   * - A plan change is scheduled in the future (not possible to undo with Google).
   * - Or subscription is in ON_HOLD state (i.e. subscription_bad)
   */
  get allowPlanChange(): boolean {
    if (this.billing_type !== 'google') {
      return true
    }
    return !this.change_plan_at_period_end && this.status !== 'subscription_bad'
  }

  /**
   * Returns true if the current billing type supports cancelling the free trial
   * to move directly to the first paid period.
   *
   * This allows users to start with the paid plan earlier to allow them to
   * export books as PDF without waiting for the trial time to end.
   */
  get supportsCancelTrial(): boolean {
    return this.billing_type === 'apple' || this.billing_type === 'google'
  }

  /**
   * Whether or not to show external payment methods on iOS.
   */
  get showExternalPayment16218(): boolean {
    return (
      this.platform === 'ios' && iosExternalLinkPayment16218Variant() === 'B'
    )
  }
}

export class AppStoreBillingView extends BillingView {
  public appStore: AppStore

  public platform: 'ios' | 'android'

  protected _stripeMonthlyPlan16218?: MonthlyPlanView
  protected _stripeAnnualPlan16218?: AnnualPlanView

  constructor(
    billing: Billing,
    appStore: AppStore,
    platform: 'ios' | 'android',
  ) {
    super(billing)
    this.appStore = appStore
    this.platform = platform
  }

  isAppStore(): boolean {
    return true
  }

  get freePlan(): PlanView {
    if (!this._freePlan) {
      this._freePlan = new FreePlanView(this.billing_plans)
    }
    return this._freePlan
  }

  get monthlyPlan(): MonthlyPlanView {
    if (!this._monthlyPlan) {
      this._monthlyPlan = MonthlyPlanView.createAppStoreView(
        this.appStore,
        this.platform,
      )
    }
    return this._monthlyPlan
  }

  get annualPlan(): AnnualPlanView {
    if (!this._annualPlan) {
      this._annualPlan = AnnualPlanView.createAppStoreView(
        this.appStore,
        this.platform,
      )
    }
    return this._annualPlan
  }

  get stripeMonthlyPlan16218(): MonthlyPlanView | null {
    if (!this.showExternalPayment16218) {
      return null
    }

    if (!this._stripeMonthlyPlan16218) {
      this._stripeMonthlyPlan16218 = MonthlyPlanView.createView(
        this.billing_plans,
      )
    }
    return this._stripeMonthlyPlan16218
  }

  get stripeAnnualPlan16218(): AnnualPlanView | null {
    if (!this.showExternalPayment16218) {
      return null
    }

    if (!this._stripeAnnualPlan16218) {
      this._stripeAnnualPlan16218 = AnnualPlanView.createView(
        this.billing_plans,
      )
    }
    return this._stripeAnnualPlan16218
  }

  /**
   * PlanView representing the billing data of the currently
   * active billing period.
   *
   * Note: Currently, this is only for the Annual Plan. In the
   * future we may expand it to include other plans.
   */
  get currentPeriodPlan(): PlanView | undefined {
    // We don't offer discounts on mobile apps.
    return
  }

  /**
   * Subscribe to the specified billing plan.
   */
  async subscribeTo(plan: PlanView): Promise<void> {
    const isNewUser = this.trialMode === 'Not Started'
    const wasCanceled = this.cancel_at_period_end === true

    if (plan.id !== 'plan_monthly' && plan.id !== 'plan_annual') {
      throw new Error(`Unexpected plan id ${plan.id}`)
    }

    const isPlanChange =
      this.plan_id !== 'plan_free' && this.plan_id !== plan.id

    let product: AppStorePluginProduct
    try {
      if (plan.id === 'plan_monthly') {
        product = await this.appStore.subscribeMonthly(isPlanChange)
      } else if (plan.id === 'plan_annual') {
        product = await this.appStore.subscribeAnnual(isPlanChange)
      }
    } catch (error) {
      if (!(error instanceof UserCancelledError)) {
        Sentry.captureException(error)

        // Refresh billing data to handle errors that were caused by stale
        // UI. E.g. user clicks 'reactivate' in our UI but had reactivated
        // through the payment provider UI just before.
        const billing = await backend.getBilling()
        this.update(billing)
      }
      throw error
    }

    // Cordova purchase plugin issues the Apple receipt verification
    // request to the backend that also updates billing information
    // for the user.
    // Here we fetch the fresh information after that update:
    const billing = await backend.getBilling()

    if (billing.plan_id !== 'plan_free') {
      await this._trackBillingEvents(billing, plan, isNewUser)
    }

    this.update(billing)
    if (!this.isPaidPlan) {
      // Exit if backend was not updated with the paid plan
      // during the purchase process.
      // This is an unexpected situation: iOS marked the product as `owned`,
      // so the receipt validation should have passed successfully.
      // We log it to Sentry, so we know if this actually happens.
      Sentry.withScope((scope) => {
        scope.setExtra('Billing data', billing)
        scope.setExtra('Product', product)
        Sentry.captureMessage(
          'Unexpected: billing plan is not paid after subscribing.',
        )
      })
      return
    }

    // Show subscribed alert for the user.
    if (isNewUser) {
      this.alert = BillingAlert.subscribedAndRedirect
    } else if (wasCanceled) {
      this.alert = BillingAlert.resubscribed
    } else if (this.change_plan_at_period_end && plan.id === 'plan_monthly') {
      this.alert = BillingAlert.downgraded
    } else if (this.change_plan_at_period_end && plan.id === 'plan_annual') {
      this.alert = BillingAlert.upgraded
    } else {
      this.alert = BillingAlert.subscribed
    }

    // Reset app cache to force API data to reload.
    // When we switch from free to paid and back, the content changes,
    // so we need to get the fresh data before showing it.
    await resetAppCache()
  }

  private async _trackBillingEvents(
    newBilling: Billing,
    newPlan: PlanView,
    isNewUser: boolean,
  ): Promise<void> {
    // Note: we could also send new user analytics in the
    // webhook handler for the SUBSCRIBED:INITIAL_BUY event.
    if (isNewUser) {
      await AdjustWrapper.trackCardSave()
      Mixpanel.trackCardSave(newBilling)
    }
    await trackSubscribe(
      {
        id: newPlan.id,
        amount: newPlan.amount,
        nickname: newPlan.nickname,
      },
      this,
    )
  }

  /**
   * Update the billing data and track events after closing the external billing page.
   */
  async updateAfterExternalPage16218(
    selectedPlan: PlanView,
    closeSheet: () => void,
  ): Promise<void> {
    const isNewUser = this.trialMode === 'Not Started'
    const updatedBilling = await backend.getBilling()

    if (updatedBilling.plan_id === 'plan_free') {
      // The plan wasn't updated.
      return
    }

    await this._trackBillingEvents(updatedBilling, selectedPlan, isNewUser)

    this.update(updatedBilling)

    if (isNewUser) {
      this.alert = BillingAlert.subscribedAndRedirect
    } else {
      this.alert = BillingAlert.subscribed
    }

    closeSheet()

    // Reset app cache to force API data to reload.
    // When we switch from free to paid and back, the content changes,
    // so we need to get the fresh data before showing it.
    await resetAppCache()
  }

  override get showDiscountBanner(): boolean {
    return false
  }
}
